import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Link01Icon, CheckmarkCircle01Icon, Alert01Icon } from '@hugeicons/core-free-icons';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const DerivCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth(); // Get current user state to preserve role
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState(null);

  useEffect(() => {
    const linkAccount = async () => {
      const params = new URLSearchParams(location.search);
      const accounts = [];
      
      // Deriv returns parameters like acct1, token1, acct2, token2, etc.
      let i = 1;
      while (params.get(`acct${i}`)) {
        accounts.push({
          loginid: params.get(`acct${i}`),
          token: params.get(`token${i}`)
        });
        i++;
      }

      if (accounts.length === 0) {
        setStatus('error');
        setError("No Deriv accounts found in the response.");
        return;
      }

      try {
        await api.post('/deriv/link-account', { accounts });
        
        // Fetch fresh status from /deriv/my-accounts to confirm linking
        const accountRes = await api.get('/deriv/my-accounts');
        
        const responseData = accountRes.data?.data || accountRes.data || {};
        const apiUser = responseData.user || {};
        const has_deriv_account = responseData.has_deriv_account;
        const deriv_account = responseData.deriv_account;
        
        if (!apiUser.id) throw new Error("Invalid user data received from server.");
        
        // Fix: Flatten role if it's an object (Crucial for preventing crashes)
        let roleValue = apiUser.role;
        if (typeof roleValue === 'object' && roleValue !== null) {
          roleValue = roleValue.name || roleValue.slug || roleValue.title || 'Unknown';
        }

        // Failsafe linking check
        const isDerivLinked = !!has_deriv_account || !!deriv_account;

        // Normalize user object for AuthContext
        const userData = {
          ...user,
          ...apiUser,
          role: roleValue,
          deriv_linked: isDerivLinked
        };

        const currentToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        login(userData, currentToken, !!localStorage.getItem('access_token'));
        
        setStatus('success');
        
        // Force hard reload to ensure AuthContext re-hydrates fresh state
        setTimeout(() => {
          if (roleValue === 'agent') {
            window.location.href = '/dashboard';
          } else {
            window.location.href = '/';
          }
        }, 1500);
      } catch (err) {
        setStatus('error');
        let msg = "Failed to link your Deriv account.";
        
        if (err.response?.data?.message) {
          msg = err.response.data.message;
        } else if (err.message) {
          msg = err.message;
        }

        // Handle specific object error {loginid, reason} to avoid React Error #31
        if (typeof msg === 'object' && msg !== null) {
          msg = msg.reason || JSON.stringify(msg);
        }

        setError(String(msg));
      }
    };

    linkAccount();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-off-white dark:bg-dark-bg px-4">
      <div className="max-w-md w-full bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg text-center">
        {status === 'processing' && (
          <div className="space-y-6">
            <div className="bg-blue-500/10 p-6 rounded-[2rem] inline-block mb-4 text-blue-500 animate-pulse">
              <HugeiconsIcon icon={Link01Icon} size={48} />
            </div>
            <h1 className="text-2xl font-black text-charcoal dark:text-white">Linking Account...</h1>
            <p className="text-muted-gray">Please wait while we securely connect your Deriv account.</p>
            <div className="flex justify-center gap-2">
              <div className="w-2 h-2 bg-soft-green rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-soft-green rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-soft-green rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6">
            <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-[2rem] inline-block mb-4 text-soft-green animate-bounce">
              <HugeiconsIcon icon={CheckmarkCircle01Icon} size={48} />
            </div>
            <h1 className="text-2xl font-black text-charcoal dark:text-white">Successfully Linked!</h1>
            <p className="text-muted-gray">Your Deriv account is now connected. Redirecting you to the dashboard...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6">
            <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-[2rem] inline-block mb-4 text-red-500">
              <HugeiconsIcon icon={Alert01Icon} size={48} />
            </div>
            <h1 className="text-2xl font-black text-charcoal dark:text-white">Linking Failed</h1>
            <p className="text-red-600 dark:text-red-400 font-bold">{error}</p>
            <button 
              onClick={() => navigate('/link-deriv')}
              className="w-full py-4 bg-soft-green text-white rounded-2xl font-black shadow-lg"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DerivCallback;